/**
 * Logging Configuration Utility
 * 
 * Provides centralized logging configuration that supports:
 * 1. Environment variables (LOG_LEVEL, DEBUG_LOGGING)
 * 2. Query parameters (?debug=true, ?logLevel=debug)
 * 3. User settings (loggingVerbosity)
 * 
 * Priority order (highest to lowest):
 * 1. Query parameters (for immediate debugging)
 * 2. User settings (for persistent user preference)
 * 3. Environment variables (for default/development setup)
 */

export type LogLevel = 'silent' | 'error' | 'warn' | 'info' | 'debug' | 'trace';

export interface LoggingConfig {
  level: LogLevel;
  enabled: boolean;
  verbose: boolean;
  consoleEnabled: boolean;
}

// Log level hierarchy (higher number = more verbose)
const LOG_LEVELS: Record<LogLevel, number> = {
  silent: 0,
  error: 1,
  warn: 2,
  info: 3,
  debug: 4,
  trace: 5,
};

/**
 * Get log level from environment variable
 */
function getEnvLogLevel(): LogLevel | null {
  if (typeof process === 'undefined' || !process.env) {
    return null;
  }

  const envLevel = process.env.LOG_LEVEL?.toLowerCase();
  if (envLevel && LOG_LEVELS[envLevel as LogLevel] !== undefined) {
    return envLevel as LogLevel;
  }

  // Check for DEBUG_LOGGING flag
  if (process.env.DEBUG_LOGGING === 'true' || process.env.DEBUG_LOGGING === '1') {
    return 'debug';
  }

  return null;
}

/**
 * Get log level from query parameters
 */
function getQueryParamLogLevel(): LogLevel | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);

  // Check for ?logLevel=debug, ?logLevel=trace, etc.
  const logLevel = params.get('logLevel')?.toLowerCase();
  if (logLevel && LOG_LEVELS[logLevel as LogLevel] !== undefined) {
    return logLevel as LogLevel;
  }

  // Check for ?debug=true (shorthand for debug level)
  if (params.get('debug') === 'true' || params.get('debug') === '1') {
    return 'debug';
  }

  // Check for ?verbose=true (shorthand for trace level)
  if (params.get('verbose') === 'true' || params.get('verbose') === '1') {
    return 'trace';
  }

  return null;
}

/**
 * Get log level from user settings
 */
function getUserSettingsLogLevel(userSettings: any): LogLevel | null {
  if (!userSettings || typeof userSettings !== 'object') {
    return null;
  }

  const verbosity = userSettings.loggingVerbosity;
  if (!verbosity) {
    return null;
  }

  // Support both string and object formats
  if (typeof verbosity === 'string') {
    const level = verbosity.toLowerCase();
    if (LOG_LEVELS[level as LogLevel] !== undefined) {
      return level as LogLevel;
    }
  }

  // Support object format: { level: 'debug', enabled: true }
  if (typeof verbosity === 'object' && verbosity.level) {
    const level = verbosity.level.toLowerCase();
    if (LOG_LEVELS[level as LogLevel] !== undefined) {
      return level as LogLevel;
    }
  }

  return null;
}

/**
 * Check if logging is enabled via query params
 */
function isQueryParamLoggingEnabled(): boolean | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const disableLogging = params.get('disableLogging');

  if (disableLogging === 'true' || disableLogging === '1') {
    return false;
  }

  return null;
}

/**
 * Check if console logging is enabled via query params
 */
function isQueryParamConsoleEnabled(): boolean | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const consoleLog = params.get('consoleLog');

  if (consoleLog === 'false' || consoleLog === '0') {
    return false;
  }

  if (consoleLog === 'true' || consoleLog === '1') {
    return true;
  }

  return null;
}

/**
 * Get the effective logging configuration
 * Priority: Query params > User settings > Environment variables > Defaults
 */
export function getLoggingConfig(userSettings?: any): LoggingConfig {
  // Get log levels from each source
  const queryLevel = getQueryParamLogLevel();
  const userLevel = getUserSettingsLogLevel(userSettings);
  const envLevel = getEnvLogLevel();

  // Determine effective log level (query params have highest priority)
  let effectiveLevel: LogLevel = queryLevel || userLevel || envLevel || 'info';

  // Check for explicit disable flags
  const queryDisable = isQueryParamLoggingEnabled();
  if (queryDisable === false) {
    effectiveLevel = 'silent';
  }

  // Determine if logging is enabled
  const enabled = effectiveLevel !== 'silent';

  // Determine verbosity (debug or trace level)
  const verbose = LOG_LEVELS[effectiveLevel] >= LOG_LEVELS.debug;

  // Check console logging preference
  const queryConsole = isQueryParamConsoleEnabled();
  let consoleEnabled = true; // Default to enabled in development

  if (queryConsole !== null) {
    consoleEnabled = queryConsole;
  } else if (userSettings != null && userSettings.loggingVerbosity && typeof userSettings.loggingVerbosity === 'object') {
    consoleEnabled = userSettings.loggingVerbosity.consoleEnabled !== false;
  } else if (typeof process !== 'undefined' && process.env) {
    // Disable console in production by default
    consoleEnabled = process.env.NODE_ENV !== 'production';
  }

  return {
    level: effectiveLevel,
    enabled,
    verbose,
    consoleEnabled,
  };
}

/**
 * Check if a log level should be output
 */
export function shouldLog(level: LogLevel, config: LoggingConfig): boolean {
  if (!config.enabled) {
    return false;
  }

  return LOG_LEVELS[level] <= LOG_LEVELS[config.level];
}

/**
 * Get current logging configuration (singleton pattern)
 * This will be updated when user settings change
 */
let currentConfig: LoggingConfig | null = null;
let currentUserSettings: any = null;

// Listener pattern to avoid circular dependencies
type ConfigListener = (config: LoggingConfig) => void;
const configListeners: ConfigListener[] = [];

export function addConfigListener(listener: ConfigListener) {
  configListeners.push(listener);
  if (currentConfig) {
    listener(currentConfig);
  }
}

export function updateLoggingConfig(userSettings?: any) {
  currentUserSettings = userSettings;
  currentConfig = getLoggingConfig(userSettings);
  
  // Notify listeners
  configListeners.forEach(listener => {
    try {
      listener(currentConfig!);
    } catch (e) {
      // Ignore listener errors
    }
  });
  
  return currentConfig;
}

export function getCurrentLoggingConfig(): LoggingConfig {
  if (!currentConfig) {
    currentConfig = getLoggingConfig(currentUserSettings);
  }
  return currentConfig;
}

// Initialize on module load
if (typeof window !== 'undefined') {
  updateLoggingConfig();

  // Watch for URL changes to update config from query params
  let lastUrl = window.location.href;
  const checkUrlChange = () => {
    if (window.location.href !== lastUrl) {
      lastUrl = window.location.href;
      updateLoggingConfig(currentUserSettings);
    }
  };

  // Check on popstate (back/forward navigation)
  window.addEventListener('popstate', checkUrlChange);

  // Check periodically (for programmatic URL changes)
  setInterval(checkUrlChange, 1000);
}
